import http from "./http";
import { API_URL, USE_MOCK } from "./config";
import { mockOrders } from "./mock/orders";
import { getUserLocal } from "./auth";

// Generated invoices: generating one gives it its number (INV2610-0001) and keeps what it showed.
//   POST order-invoices/preview { orderIds }          -> the invoice as it would be, without number (nothing saved)
//   POST order-invoices { orderIds }                  -> invoice   (the orders of ONE customer)
//   GET  order-invoices?searchString&fromDate&toDate&b2bUserId&page&limit -> { data: [invoice], meta }
//   GET  order-invoices/:id                           -> invoice
//   GET  order-invoices/by-no/:invoiceNo              -> invoice
// invoice: { id, invoiceNo, customerCode, customerName, customerPhone, customerAddress, deliveryMethod, orderCount,
//   productTotal, quantity, weight, cnCourier, shipping, bdCourier, other, discount, total, paid, due,
//   lines: [{ code, orderDate, product, quantity, weight, cnCourier, shipping, bdCourier, other, discount, total, paid, due }],
//   orderCodes, createdBy, createdDateTime }
const apiUrl = `${API_URL}order-invoices`;

const delay = (value) => new Promise((resolve) => setTimeout(() => resolve(value), 250));
const num = (value) => Number(value) || 0;
const sum = (rows, key) => rows.reduce((total, row) => total + num(row[key]), 0);

/* ----- mock mode: the same figures the API works out, kept in memory for the session ----- */
const mockInvoices = [];

const mockLine = (order) => {
  const shipment = order.shipment || {};
  const total = num(order.state?.totalAmount);
  const paid = num(order.paidAmount);
  const cnCourier = num(shipment.userForeignEquivalentAmount);
  const bdCourier = num(shipment.userLocalAmount);
  const shipping = Math.max(num(shipment.userForeignToLocalEquivalentAmount), num(order.shippingCost) - cnCourier - bdCourier);
  return {
    orderId: order.id,
    code: order.code,
    orderDate: order.orderDate,
    status: order.status,
    product: num(order.state?.subTotal),
    quantity: num(order.state?.totalQuantity),
    weight: num(shipment.userWeight),
    cnCourier,
    shipping,
    bdCourier,
    other: num(order.orderTax) + num(order.otherCharge),
    discount: num(order.orderDiscount),
    total,
    paid,
    due: Math.max(total - paid, 0),
  };
};

// the invoice of these orders, without a number (what the summary before generating shows)
const mockBuild = (orderIds) => {
  const orders = orderIds.map((id) => mockOrders.find((order) => order.id === Number(id))).filter(Boolean);
  if (!orders.length) throw new Error("Select at least one order");
  if (new Set(orders.map((order) => order.b2bUserId)).size > 1) throw new Error("An invoice is for the orders of one customer");

  const first = orders[0];
  const lines = orders.map(mockLine);
  const now = new Date();
  const user = getUserLocal();
  return {
    b2bUserId: first.b2bUserId,
    customerCode: first.b2bUser?.identificationShortCode,
    customerName: first.b2bUser?.companyName || first.customerName,
    customerPhone: first.customerPhone,
    customerAddress: first.deliveryAddress || first.address,
    deliveryMethod: [...new Set(orders.map((order) => order.deliveryMethod).filter(Boolean))].join(", "),
    orderCount: lines.length,
    productTotal: sum(lines, "product"),
    quantity: sum(lines, "quantity"),
    weight: sum(lines, "weight"),
    cnCourier: sum(lines, "cnCourier"),
    shipping: sum(lines, "shipping"),
    bdCourier: sum(lines, "bdCourier"),
    other: sum(lines, "other"),
    discount: sum(lines, "discount"),
    total: sum(lines, "total"),
    paid: sum(lines, "paid"),
    due: sum(lines, "due"),
    lines,
    orderCodes: lines.map((line) => line.code),
    createdBy: [user?.firstname, user?.lastname].filter(Boolean).join(" ") || "Admin",
    createdDateTime: now.toISOString(),
  };
};

const mockGenerate = (orderIds) => {
  const draft = mockBuild(orderIds);
  const prefix = `INV${draft.createdDateTime.slice(2, 4)}${draft.createdDateTime.slice(5, 7)}-`;
  const made = mockInvoices.filter((invoice) => invoice.invoiceNo.startsWith(prefix)).length;
  const invoice = { ...draft, id: mockInvoices.length + 1, invoiceNo: `${prefix}${String(made + 1).padStart(4, "0")}` };
  mockInvoices.unshift(invoice);
  return invoice;
};

/* ----- API ----- */

// what the invoice of these orders will show (customer, lines, totals) - nothing is saved, no number is taken
export async function previewInvoice(orderIds) {
  const ids = [...new Set(orderIds.map(Number))];
  if (USE_MOCK) return delay(mockBuild(ids));
  const { data } = await http.post(`${apiUrl}/preview`, { orderIds: ids });
  return data.payload;
}

// generate one invoice of these orders (one customer) -> the invoice, with its number
export async function generateInvoice(orderIds) {
  const ids = [...new Set(orderIds.map(Number))];
  if (USE_MOCK) return delay(mockGenerate(ids));
  const { data } = await http.post(apiUrl, { orderIds: ids });
  return data.payload;
}

export async function getInvoices({ searchString, fromDate, toDate, b2bUserId, page = 1, limit = 20 } = {}) {
  if (USE_MOCK) {
    const needle = String(searchString || "").trim().toLowerCase();
    const rows = mockInvoices
      .filter((invoice) => !b2bUserId || String(invoice.b2bUserId) === String(b2bUserId))
      .filter((invoice) => !fromDate || invoice.createdDateTime.slice(0, 10) >= fromDate)
      .filter((invoice) => !toDate || invoice.createdDateTime.slice(0, 10) <= toDate)
      .filter(
        (invoice) =>
          !needle || [invoice.invoiceNo, invoice.customerName, invoice.customerCode, ...invoice.orderCodes].some((value) => String(value || "").toLowerCase().includes(needle))
      );
    return delay({ data: rows.slice((page - 1) * limit, page * limit), meta: { total: rows.length, totalPages: Math.max(1, Math.ceil(rows.length / limit)) } });
  }

  const { data } = await http.get(apiUrl, {
    params: { page, limit, ...(searchString && { searchString }), ...(fromDate && { fromDate }), ...(toDate && { toDate }), ...(b2bUserId && { b2bUserId }) },
  });
  const { data: rows = [], meta = {} } = data.payload;
  return { data: rows, meta: { total: meta.totalRecords ?? rows.length, totalPages: meta.totalPages ?? 1 } };
}

export async function getInvoice(id) {
  if (USE_MOCK) return delay(mockInvoices.find((invoice) => invoice.id === Number(id)));
  const { data } = await http.get(`${apiUrl}/${id}`);
  return data.payload;
}
